import { filter, identity, partial } from 'ramda'
import { createSelector } from 'redux-bundler'

import { repr } from '~/src/Lib/Utils'

/**
 * Wrapper function returned by kwargsValidationWrapper that ensures that the correct kwargs are provided.
 * @typedef {function} KwargsValidationWrapper
 * @param {Object} kwargs
 * @param {Array} kwargs.dependencies - The dependencies to pass to createSelector
 * @param {function} kwargs.resultFn - The result function to pass to createSelector
 * @param {function[]} [tests] - Additional tests to run on the kwargs
 */

/**
 * @typedef {Function} Selector
 * @property {function} resultFunc - The inner result function that accepts the selected dependencies
 * @returns {any} - The result of the selector
 */

/**
 *
 * @param {*} fn
 * @param  {...any} validators
 * @returns {KwargsValidationWrapper}
 */
export const kwargsValidationWrapper = (fn, ...validators) => (kwargs, tests = validators) => {
  const { dependencies, resultFn } = kwargs
  if (!Array.isArray(dependencies)) {
    throw new TypeError(`"dependencies" must be an array. Got ${repr(dependencies)} instead.`)
  }
  if (typeof resultFn !== 'function') {
    throw new TypeError(`"resultFn" must be a function. Got ${repr(resultFn)} instead.`)
  }
  tests.forEach(validator => validator(kwargs))
  return fn(kwargs)
}

/**
 * Returns default value if guardFn returns false. Primary use case is guarded reactors.
 * @type {KwargsValidationWrapper}
 * @returns {Selector}
 */
export const createGuardedSelector = kwargsValidationWrapper(({
  dependencies,
  resultFn,
  defaultReturnValue = null,
  guardDependencies,
  guardFn,
}) => {
  const guardedResultFn = (...allArgs) => {
    const guardArgs = allArgs.slice(0, guardDependencies.length)
    const dependencyArgs = allArgs.slice(guardDependencies.length)
    if (!guardFn(...guardArgs)) {
      return defaultReturnValue
    }
    return resultFn(...dependencyArgs)
  }
  return createSelector(...[...guardDependencies, ...dependencies], guardedResultFn)
}, ({ guardDependencies }) => {
  if (!Array.isArray(guardDependencies)) {
    throw new TypeError(`"guardDependencies" must be an array. Got ${repr(guardDependencies)} instead.`)
  }
}, ({ guardFn }) => {
  if (typeof guardFn !== 'function') {
    throw new TypeError(`"guardFn" must be a function. Got ${repr(guardFn)} instead.`)
  }
})

/**
 * Returns default value if user is not authenticated. Primary use case is guarded reactors.
 * @type {KwargsValidationWrapper}
 * @returns {Selector}
 */
export const createAuthenticatedSelector = kwargs => createGuardedSelector({
  ...kwargs,
  guardDependencies: ['selectAuth'],
  guardFn: auth => auth?.authenticated ?? false,
})

/**
 * Returns default value if app is not ready. Primary use case is guarded reactors.
 * @type {KwargsValidationWrapper}
 * @returns {Selector}
 */
export const createAppIsReadySelector = kwargs => createGuardedSelector({
  ...kwargs,
  guardDependencies: ['selectAppIsReady'],
  guardFn: identity,
})

export const createScopedSelector = kwargsValidationWrapper(({
  dependencies,
  resultFn,
  scope,
  defaultReturnValue = null,
}) => createSelector(
  'selectAppIsReady',
  scope.selector,
  ...dependencies,
  (appIsReady, scopeSelection, ...dependencyArgs) => {
    if (!appIsReady) return defaultReturnValue
    const unfiltered = resultFn(...dependencyArgs)
    return filter(partial(scope.test, [scopeSelection]), unfiltered)
  }
), ({ scope }) => {
  if (!scope) throw new TypeError('"scope" must be defined')
  if (typeof scope.test !== 'function') {
    throw new TypeError(`"scope.test" must be a function. Got ${repr(scope.test)} instead.`)
  }

  const { selector } = scope
  const selectorType = typeof selector
  if (selectorType !== 'function' && selectorType !== 'string') {
    throw new TypeError(`"scope.selector" must be a function or selector name. Got ${repr(selector)} instead.`)
  }
  if (selectorType === 'string' && !selector.startsWith('select')) {
    throw new TypeError(`"scope.selector" must be a selector name. Got ${repr(selector)} instead.`)
  }
})
